import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Header from '../components/Header';
import { Box, Button, Typography, Card, CardContent, CardActions, Grid, Container, CircularProgress } from '@mui/material';
import { listUnspent } from '../webservice/mProviders';
import { homenetwork } from '../Headerr';
import { bsv } from 'scrypt-ts';
import { Chip } from '@mui/material';
interface AssetData {
  txId: string;
  outputIndex: number;
  satoshis: number;
  script: string;
  tokenData?: any;
}

const parseTokenScript = (scriptHex: string) => {
  try {
    const asm = bsv.Script.fromHex(scriptHex).toASM();
    if (asm.endsWith('OP_DROP') && asm.includes('OP_CHECKSIG')) {
      const parts = asm.split(' ');
      const checkSigIndex = parts.indexOf('OP_CHECKSIG');
      const dropIndex = parts.lastIndexOf('OP_DROP');
      
      if (checkSigIndex !== -1 && dropIndex !== -1 && dropIndex > checkSigIndex) {
        const dataHexParts = parts.slice(checkSigIndex + 1, dropIndex);
        if (dataHexParts.length === 1) {
          const dataHex = dataHexParts[0];
          let str = '';
          for (let i = 0; i < dataHex.length; i += 2) {
            str += String.fromCharCode(parseInt(dataHex.substring(i, i + 2), 16));
          }
          try {
            return JSON.parse(str);
          } catch (e) {
            return { rawData: str };
          }
        }
      }
    }
  } catch (e) {
    // Ignore errors
  }
  return undefined;
};

const Assets: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [assets, setAssets] = useState<AssetData[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(true);

  const fetchAssets = useCallback(async () => {
    const cachedAddress = localStorage.getItem('wallet_address');
    
    if (!cachedAddress) {
      setIsAuthenticated(false);
      return;
    }
    
    setIsAuthenticated(true);
    
    const cachedAssets = localStorage.getItem('wallet_assets');
    if (cachedAssets) {
      // Exibe o cache inicialmente para feedback rápido
      setAssets(JSON.parse(cachedAssets));
    }

    setLoading(true);
    setError('');

    try {
      let correctNetwork = homenetwork;
      try {
        correctNetwork = bsv.Address.fromString(cachedAddress).network;
      } catch (e) {
        console.warn('Could not parse network from cached address', e);
      }
      const unspent = await listUnspent(cachedAddress, correctNetwork);
      
      if (!unspent || (unspent.length > 0 && unspent[0].outputIndex === -1)) {
        throw new Error(t('assetsList.errApi'));
      }

      const mappedAssets: AssetData[] = unspent.map((utxo: any) => ({
        txId: utxo.txId,
        outputIndex: utxo.outputIndex,
        satoshis: utxo.satoshis,
        script: utxo.script,
        tokenData: parseTokenScript(utxo.script)
      }));

      setAssets(mappedAssets);
      localStorage.setItem('wallet_assets', JSON.stringify(mappedAssets));
    } catch (err: any) {
      console.error(err);
      setError(t('assetsList.errServer'));
      // Don't clear assets here so they can at least see cached ones if offline
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchAssets();
  }, [fetchAssets]);

  if (!isAuthenticated) {
    return (
      <>
        <Header />
        <Container maxWidth="md" sx={{ mt: 4, textAlign: 'center' }}>
          <Typography variant="h5" gutterBottom>
            {t('assetsList.notConfigured')}
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            {t('assetsList.needSetup')}
          </Typography>
          <Button variant="contained" color="primary" onClick={() => navigate('/')}>
            {t('assetsList.goCreds')}
          </Button>
        </Container>
      </>
    );
  }

  return (
    <>
      <Header />
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Typography variant="h4" gutterBottom>
          {t('assetsList.myAssets')}
        </Typography>

        {error && (
          <Box sx={{ mb: 3 }}>
            <Typography color="error" sx={{ mb: 1 }}>
              {error}
            </Typography>
            <Button variant="outlined" color="primary" onClick={fetchAssets}>
              {t('assetsList.tryAgain')}
            </Button>
          </Box>
        )}

        {loading ? (
          <Box display="flex" alignItems="center" gap={2}>
            <CircularProgress size={24} />
            <Typography>{t('assetsList.fetching')}</Typography>
          </Box>
        ) : (
          <>
            <Grid container spacing={3}>
              {assets.map((asset, idx) => (
                <Grid size={{ xs: 12, sm: 6, md: 4 }} key={`${asset.txId}-${asset.outputIndex}`}>
                  <Card>
                    <CardContent>
                      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                        <Typography variant="h6" noWrap title={asset.txId} sx={{ maxWidth: '80%' }}>
                          TXID: {asset.txId.substring(0, 10)}...
                        </Typography>
                        {asset.tokenData && (
                          <Chip label="Token" color="secondary" size="small" />
                        )}
                      </Box>
                      
                      {asset.tokenData ? (
                        <>
                          <Typography color="textPrimary" fontWeight="bold">
                            {asset.tokenData.title || 'Unknown Token'}
                          </Typography>
                          {asset.tokenData.author && (
                            <Typography variant="body2" color="textSecondary">
                              Author: {asset.tokenData.author}
                            </Typography>
                          )}
                          {asset.tokenData.action && (
                            <Typography variant="body2" color="textSecondary">
                              Action: {asset.tokenData.action}
                            </Typography>
                          )}
                        </>
                      ) : (
                        <Typography color="textSecondary">
                          Index: {asset.outputIndex}
                        </Typography>
                      )}
                      
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        {t('assetsList.balance')} {asset.satoshis} sats
                      </Typography>
                    </CardContent>
                    <CardActions>
                      <Button 
                        size="small" 
                        color="primary" 
                        onClick={() => navigate(`/assets/${asset.txId}`)}
                      >
                        {t('assetsList.viewDetails')}
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
            
            {assets.length === 0 && !error && (
              <Typography sx={{ mt: 2 }}>{t('assetsList.notFound')}</Typography>
            )}
          </>
        )}
      </Container>
    </>
  );
};

export default Assets;
